// Player inventory: unopened packs and future stashable items.

export const INVENTORY_STATE_VERSION = 1;
export const LS_INVENTORY_KEY = "mmu_inventory_v1";
export const INVENTORY_ITEM_CAP = 99;

export const INVENTORY_ITEM_IDS = {
    CORGI_PACK: "corgi_pack",
    BOOST_REVEAL: "boost_reveal",
    BOOST_SECOND_CHANCE: "boost_second_chance",
    BOOST_FREEZE: "boost_freeze",
};

export const INVENTORY_ITEM_CATALOG = {
    [INVENTORY_ITEM_IDS.CORGI_PACK]: {
        id: INVENTORY_ITEM_IDS.CORGI_PACK,
        name: "Corgi Pack",
        pluralName: "Corgi Packs",
        description: "Open for 5 random Collection cards.",
    },
    [INVENTORY_ITEM_IDS.BOOST_REVEAL]: {
        id: INVENTORY_ITEM_IDS.BOOST_REVEAL,
        name: "Reveal",
        pluralName: "Reveals",
        description: "Highlight one unpicked card during a run.",
    },
    [INVENTORY_ITEM_IDS.BOOST_SECOND_CHANCE]: {
        id: INVENTORY_ITEM_IDS.BOOST_SECOND_CHANCE,
        name: "Shield",
        pluralName: "Shields",
        description: "Forgive the next mistake without spending a heart.",
    },
    [INVENTORY_ITEM_IDS.BOOST_FREEZE]: {
        id: INVENTORY_ITEM_IDS.BOOST_FREEZE,
        name: "Freeze",
        pluralName: "Freezes",
        description: "Hold the board still after your next correct pick.",
    },
};

export const BOOST_INVENTORY_ITEM_IDS = [
    INVENTORY_ITEM_IDS.BOOST_REVEAL,
    INVENTORY_ITEM_IDS.BOOST_SECOND_CHANCE,
    INVENTORY_ITEM_IDS.BOOST_FREEZE,
];

export function isBoostInventoryItemId(id) {
    return BOOST_INVENTORY_ITEM_IDS.includes(String(id || ""));
}

export function createEmptyInventory() {
    return {
        version: INVENTORY_STATE_VERSION,
        items: {
            [INVENTORY_ITEM_IDS.CORGI_PACK]: 0,
            [INVENTORY_ITEM_IDS.BOOST_REVEAL]: 0,
            [INVENTORY_ITEM_IDS.BOOST_SECOND_CHANCE]: 0,
            [INVENTORY_ITEM_IDS.BOOST_FREEZE]: 0,
        },
    };
}

function clampItemCount(value) {
    return Math.max(
        0,
        Math.min(INVENTORY_ITEM_CAP, Math.floor(Number(value) || 0))
    );
}

export function normalizeInventory(raw = null) {
    const empty = createEmptyInventory();
    if (!raw || typeof raw !== "object") {
        return empty;
    }

    const sourceItems =
        raw.items && typeof raw.items === "object" ? raw.items : raw;
    const items = { ...empty.items };

    Object.keys(INVENTORY_ITEM_CATALOG).forEach((id) => {
        items[id] = clampItemCount(sourceItems[id]);
    });

    // Preserve unknown future item ids so newer clients can round-trip them.
    Object.keys(sourceItems || {}).forEach((id) => {
        if (items[id] === undefined) {
            items[id] = clampItemCount(sourceItems[id]);
        }
    });

    return {
        version: INVENTORY_STATE_VERSION,
        items,
    };
}

export function mergeInventory(localRaw = null, remoteRaw = null) {
    const local = normalizeInventory(localRaw);
    const remote = normalizeInventory(remoteRaw);
    const ids = new Set([
        ...Object.keys(local.items),
        ...Object.keys(remote.items),
    ]);
    const items = {};

    ids.forEach((id) => {
        items[id] = Math.max(
            clampItemCount(local.items[id]),
            clampItemCount(remote.items[id])
        );
    });

    return normalizeInventory({ items });
}

export function getInventoryItemCount(inventory = null, itemId = "") {
    const normalized = normalizeInventory(inventory);
    const id = String(itemId || "").trim();
    return clampItemCount(normalized.items[id]);
}

export function getCorgiPackCount(inventory = null) {
    return getInventoryItemCount(inventory, INVENTORY_ITEM_IDS.CORGI_PACK);
}

export function grantInventoryItem(inventory = null, itemId = "", amount = 1) {
    const normalized = normalizeInventory(inventory);
    const id = String(itemId || "").trim();
    const add = Math.max(0, Math.floor(Number(amount) || 0));
    if (!id || add <= 0) {
        return normalized;
    }

    return normalizeInventory({
        items: {
            ...normalized.items,
            [id]: clampItemCount((normalized.items[id] || 0) + add),
        },
    });
}

export function grantCorgiPacks(inventory = null, amount = 1) {
    return grantInventoryItem(
        inventory,
        INVENTORY_ITEM_IDS.CORGI_PACK,
        amount
    );
}

export function spendInventoryItem(inventory = null, itemId = "", amount = 1) {
    const normalized = normalizeInventory(inventory);
    const id = String(itemId || "").trim();
    const cost = Math.max(0, Math.floor(Number(amount) || 0));
    const have = clampItemCount(normalized.items[id]);

    if (!id || cost <= 0) {
        return { ok: false, inventory: normalized, spent: 0 };
    }
    if (have < cost) {
        return { ok: false, inventory: normalized, spent: 0 };
    }

    return {
        ok: true,
        spent: cost,
        inventory: normalizeInventory({
            items: {
                ...normalized.items,
                [id]: clampItemCount(have - cost),
            },
        }),
    };
}

export function spendCorgiPacks(inventory = null, amount = 1) {
    return spendInventoryItem(
        inventory,
        INVENTORY_ITEM_IDS.CORGI_PACK,
        amount
    );
}

export function formatInventoryItemCount(itemId = "", count = 0) {
    const meta = INVENTORY_ITEM_CATALOG[itemId];
    const amount = clampItemCount(count);
    if (!meta) {
        return `${amount}× ${itemId}`;
    }
    if (amount === 1) {
        return `1 ${meta.name}`;
    }
    return `${amount} ${meta.pluralName}`;
}

export function listOwnedInventoryEntries(inventory = null) {
    const normalized = normalizeInventory(inventory);
    return Object.keys(normalized.items)
        .map((id) => {
            const count = clampItemCount(normalized.items[id]);
            if (count <= 0) {
                return null;
            }
            const meta = INVENTORY_ITEM_CATALOG[id] || {
                id,
                name: id,
                pluralName: id,
                description: "",
            };
            return {
                ...meta,
                count,
                label: formatInventoryItemCount(id, count),
            };
        })
        .filter(Boolean)
        .sort((a, b) => a.name.localeCompare(b.name));
}

export function readGuestInventory() {
    try {
        const raw = window.localStorage.getItem(LS_INVENTORY_KEY);
        if (!raw) {
            return createEmptyInventory();
        }
        return normalizeInventory(JSON.parse(raw));
    } catch {
        return createEmptyInventory();
    }
}

export function saveGuestInventory(inventory = null) {
    try {
        window.localStorage.setItem(
            LS_INVENTORY_KEY,
            JSON.stringify(normalizeInventory(inventory))
        );
    } catch {
        // ignore localStorage failures
    }
}
